import { LaunchTask } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import {
  ExpandableSection,
  StatusIndicator,
  StatusIndicatorProps,
} from '@amzn/awsui-components-console';
import { BuildStatusIndicator } from './BuildStatusIndicator';
import { BUILD_LINK } from '../../../constants';
import { linkify } from '../../../components/linkify';

interface BuildIdsMapComponentProps {
  buildsMap: LaunchTask['buildIdsMap'] | null;
}

enum LaunchTaskBuildStageEnum {
  STAGING_VERSION_SET_BUILD = 'STAGING_VERSION_SET_BUILD',
  STAGING_VERSION_SET_REMOVAL = 'STAGING_VERSION_SET_REMOVAL',
  VENUE_VERSION_SET_BUILD = 'VENUE_VERSION_SET_BUILD',
}

// The recognized, progressive shapes of buildIdsMap. Anything else (empty,
// an unexpected key, or an unrecognized combination of stages) normalizes to
// "no data" — rendered as "No data available." with an overall "Not Started"
// status.
type BuildsShape = 'STAGING' | 'STAGING_REMOVAL' | 'ALL';

// The single roll-up status shown in the collapsed header so users get the
// overall build result at a glance without reading every intermediate stage.
type OverallBuildStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'SKIPPED'
  | 'SUCCEEDED'
  | 'FAILED';

// Raw per-build statuses (buildIdsMap[stage].status) that count as a failure.
// Mirrors the error-styled statuses in BuildStatusIndicator.
const FAILED_BUILD_STATUSES: ReadonlySet<string> = new Set([
  'FAILED',
  'SUBMIT_FAILED',
  'CANCELED',
]);

const overallStatusIndicatorProps: Record<
  OverallBuildStatus,
  StatusIndicatorProps
> = {
  NOT_STARTED: { type: 'not-started', children: 'Not Started' },
  IN_PROGRESS: { type: 'in-progress', children: 'In Progress' },
  SKIPPED: { type: 'info', children: 'Skipped' },
  SUCCEEDED: { type: 'success', children: 'Succeeded' },
  FAILED: { type: 'error', children: 'Failed' },
};

export const BuildIdsMapComponent: React.FC<BuildIdsMapComponentProps> = ({
  buildsMap,
}) => {
  const shape = getBuildsShape(buildsMap);
  const overall = computeOverallStatus(buildsMap, shape);

  // Collapsed by default: the header surfaces only the overall status, and the
  // full per-build breakdown stays available behind the expander.
  return (
    <ExpandableSection
      variant='container'
      headerText='FLC builds'
      headerActions={
        <StatusIndicator {...overallStatusIndicatorProps[overall]} />
      }
    >
      {renderBuildDetails(buildsMap, shape)}
    </ExpandableSection>
  );
};

// Determine which recognized shape the buildsMap matches, or null when it has
// no data, an unexpected key, or an unrecognized combination of stages.
function getBuildsShape(
  buildsMap: BuildIdsMapComponentProps['buildsMap']
): BuildsShape | null {
  if (!buildsMap || Object.keys(buildsMap).length === 0) {
    return null;
  }

  const buildsKeys = Object.keys(buildsMap).sort();

  for (const key of buildsKeys) {
    if (
      !Object.values(LaunchTaskBuildStageEnum).includes(
        key as LaunchTaskBuildStageEnum
      )
    ) {
      return null;
    }
  }

  const stagingOnly = [
    LaunchTaskBuildStageEnum.STAGING_VERSION_SET_BUILD,
  ].sort();
  if (
    buildsKeys.length === 1 &&
    areArraysEqualUnordered(buildsKeys, stagingOnly)
  ) {
    return 'STAGING';
  }

  const stagingAndRemoval = [
    LaunchTaskBuildStageEnum.STAGING_VERSION_SET_BUILD,
    LaunchTaskBuildStageEnum.STAGING_VERSION_SET_REMOVAL,
  ].sort();
  if (
    buildsKeys.length === 2 &&
    areArraysEqualUnordered(buildsKeys, stagingAndRemoval)
  ) {
    return 'STAGING_REMOVAL';
  }

  const allBuilds = [
    LaunchTaskBuildStageEnum.STAGING_VERSION_SET_BUILD,
    LaunchTaskBuildStageEnum.STAGING_VERSION_SET_REMOVAL,
    LaunchTaskBuildStageEnum.VENUE_VERSION_SET_BUILD,
  ].sort();
  if (
    buildsKeys.length === 3 &&
    areArraysEqualUnordered(buildsKeys, allBuilds)
  ) {
    return 'ALL';
  }

  return null;
}

// Roll the per-build statuses up into a single overall status for the header.
// Precedence:
//   1. No / invalid data                 -> Not Started
//   2. Any build failed (override)       -> Failed
//   3. Venue build (3/3) done            -> Succeeded
//   4. Venue build (3/3) skipped         -> Skipped
//   5. Otherwise (1/3 started, 3/3 not   -> In Progress
//      yet done or not present)
function computeOverallStatus(
  buildsMap: BuildIdsMapComponentProps['buildsMap'],
  shape: BuildsShape | null
): OverallBuildStatus {
  if (!buildsMap || !shape) {
    return 'NOT_STARTED';
  }

  const statuses = Object.values(buildsMap)
    .map((stage) => stage?.status)
    .filter((status): status is string => !!status);

  if (statuses.some((status) => FAILED_BUILD_STATUSES.has(status))) {
    return 'FAILED';
  }

  if (shape === 'ALL') {
    const venueStatus =
      buildsMap[LaunchTaskBuildStageEnum.VENUE_VERSION_SET_BUILD]?.status;
    if (venueStatus === 'DONE') {
      return 'SUCCEEDED';
    }
    if (venueStatus === 'SUBMIT_SKIPPED') {
      return 'SKIPPED';
    }
  }

  return 'IN_PROGRESS';
}

// The full, verbose per-build breakdown revealed when the section is expanded.
function renderBuildDetails(
  buildsMap: BuildIdsMapComponentProps['buildsMap'],
  shape: BuildsShape | null
) {
  if (!buildsMap || !shape) {
    return <p>No data available.</p>;
  }

  return (
    <>
      <h4>Staging VS Build (1/3)</h4>
      <BuildIdsInnerMap
        value={buildsMap[LaunchTaskBuildStageEnum.STAGING_VERSION_SET_BUILD]}
      />
      {(shape === 'STAGING_REMOVAL' || shape === 'ALL') && (
        <>
          <h4>Staging VS Removal (2/3)</h4>
          <BuildIdsInnerMap
            value={
              buildsMap[LaunchTaskBuildStageEnum.STAGING_VERSION_SET_REMOVAL]
            }
          />
        </>
      )}
      {shape === 'ALL' && (
        <>
          <h4>Venue VS Build (3/3)</h4>
          <BuildIdsInnerMap
            value={buildsMap[LaunchTaskBuildStageEnum.VENUE_VERSION_SET_BUILD]}
          />
        </>
      )}
    </>
  );
}

const BuildIdsInnerMap: React.FC<{
  value: Record<string, string>;
}> = ({ value }) => {
  return (
    <ul>
      {Object.entries(value).map(([innerKey, innerValue]) =>
        makeInnerMapListItem(innerKey, innerValue)
      )}
    </ul>
  );
};

function areArraysEqualUnordered(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }
  const set2 = new Set(arr2);
  // Check if every element in arr1 exists in set2
  return arr1.every((item) => set2.has(item));
}

function makeInnerMapListItem(key: string, value: string) {
  if (value.length == 0) {
    return <div />;
  }
  if (key == 'id') {
    return (
      <li key={key}>
        <strong>Build ID:</strong>{' '}
        <a href={BUILD_LINK.concat(value)}>{value}</a>
      </li>
    );
  }
  if (key == 'status') {
    return (
      <li key={key}>
        <strong>Status:</strong> <BuildStatusIndicator status={value} />
      </li>
    );
  }
  if (key == 'message') {
    return (
      <li key={key}>
        <strong>Message:</strong> {linkify(value)}
      </li>
    );
  }
  return (
    <li key={key}>
      <strong>{key}:</strong> {linkify(value)}
    </li>
  );
}
